import type { GroupChannel } from "@sendbird/chat/groupChannel";
import {
  AdminMessage,
  FileMessage,
  UserMessage,
} from "@sendbird/chat/message";
import { DateTime } from "luxon";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import { getMenteeDisplayPhoto } from "@/lib/redacted-display";
import { shouldShowCheckinBadge } from "@/lib/utils/check-in-badge";
import {
  conversationNeedsResponse,
  type NoticeMentorshipSignals,
} from "./notice-badge";
import { resolveNoticeBadgeSignals } from "./notice-badge-resolution";
import type { NoticeConversation } from "./notice-conversations";
import {
  isInboundAdminLastMessage,
  isMentorResponseLastMessage,
} from "./notice-inbound-message";
import { isNewTeenWindow } from "./notice-new-teen";

function getChannelTitle(channel: GroupChannel, currentUserId: string): string {
  const names = channel.members
    .filter((member) => member.userId !== currentUserId)
    .map((member) => member.nickname || member.userId)
    .filter((name) => name.length > 0);

  return names.length === 0 ? "(No members)" : names.join(", ");
}

function getMessagePreview(
  channel: GroupChannel,
  currentUserId: string,
): string {
  const lastMessage = channel.lastMessage;
  if (!lastMessage) return "No messages";

  let text = "";
  let senderId: string | undefined;

  if (lastMessage instanceof UserMessage) {
    text = lastMessage.message;
    senderId = lastMessage.sender?.userId;
  } else if (lastMessage instanceof AdminMessage) {
    text = lastMessage.message;
  } else if (lastMessage instanceof FileMessage) {
    text = lastMessage.name ?? "Attachment";
    senderId = lastMessage.sender?.userId;
  }

  if (text.length === 0) return "No messages";

  const fromSelf = senderId === currentUserId;
  return fromSelf ? `You: ${text}` : text;
}

function getLastMessageSenderId(channel: GroupChannel): string | undefined {
  const lastMessage = channel.lastMessage;
  if (!lastMessage || lastMessage instanceof AdminMessage) {
    return undefined;
  }

  return (lastMessage as { sender?: { userId?: string } }).sender?.userId;
}

function isLastMessageFromPeer(
  channel: GroupChannel,
  currentUserId: string,
): boolean {
  if (isInboundAdminLastMessage(channel.lastMessage, currentUserId)) {
    return true;
  }
  const senderId = getLastMessageSenderId(channel);
  return Boolean(senderId) && senderId !== currentUserId;
}

function isNoticeUnread(channel: GroupChannel): boolean {
  return channel.unreadMessageCount > 0;
}

function isChannelOnline(channel: GroupChannel, currentUserId: string): boolean {
  const otherMembers = channel.members.filter(
    (member) => member.userId !== currentUserId,
  );

  return (
    otherMembers.some((member) => member.connectionStatus === "online") ||
    channel.isTyping
  );
}

function getSendbirdPeerProfileUrl(
  channel: GroupChannel,
  currentUserId: string,
): string | null {
  const other = channel.members.find((member) => member.userId !== currentUserId);
  const profileUrl = other?.profileUrl;
  return profileUrl && profileUrl.length > 0 ? profileUrl : null;
}

export function resolveNoticeAvatarUrl(
  channel: GroupChannel,
  currentUserId: string,
  mentorshipAvatarUrl?: string | null,
): string | null {
  // `undefined` means no mentorship row; `null` means mentorship with no photo.
  if (mentorshipAvatarUrl !== undefined) {
    return mentorshipAvatarUrl && mentorshipAvatarUrl.length > 0
      ? mentorshipAvatarUrl
      : null;
  }
  return getSendbirdPeerProfileUrl(channel, currentUserId);
}

export function buildMenteeAvatarByUrl(
  mentorships: MentorshipDetails[],
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const mentorship of mentorships) {
    map.set(
      mentorship.sdk_url,
      getMenteeDisplayPhoto(mentorship.mentee) ?? null,
    );
  }
  return map;
}

export interface ToNoticeConversationOptions {
  now?: DateTime;
  startedAt?: string | null;
}

export function toNoticeConversation(
  channel: GroupChannel,
  currentUserId: string,
  mentorship?: NoticeMentorshipSignals,
  mentorshipAvatarUrl?: string | null,
  enableNeedsResponse = false,
  options: ToNoticeConversationOptions = {},
): NoticeConversation {
  const lastMessageAtMs = channel.lastMessage?.createdAt ?? channel.createdAt;
  const now = options.now ?? DateTime.now();
  const needsResponse =
    enableNeedsResponse &&
    conversationNeedsResponse(
      isLastMessageFromPeer(channel, currentUserId),
      mentorship,
      isMentorResponseLastMessage(channel.lastMessage, currentUserId),
    );
  const { checkIn, badge } = resolveNoticeBadgeSignals(
    shouldShowCheckinBadge(channel, currentUserId),
    mentorship,
    needsResponse,
  );

  return {
    id: channel.url,
    name: getChannelTitle(channel, currentUserId),
    preview: getMessagePreview(channel, currentUserId),
    lastMessageAt: DateTime.fromMillis(lastMessageAtMs).toISO() ?? "",
    online: isChannelOnline(channel, currentUserId),
    unread: isNoticeUnread(channel),
    needsResponse,
    checkIn,
    newTeen:
      isNewTeenWindow(channel.createdAt, now) ||
      (options.startedAt ? isNewTeenWindow(options.startedAt, now) : false),
    badge,
    avatarUrl: resolveNoticeAvatarUrl(
      channel,
      currentUserId,
      mentorshipAvatarUrl,
    ),
  };
}

export function toNoticeConversations(
  channels: GroupChannel[],
  currentUserId: string,
  mentorshipByUrl?: Map<string, NoticeMentorshipSignals>,
  avatarByUrl?: Map<string, string | null>,
  enableNeedsResponse = false,
  options: {
    now?: DateTime;
    startedAtByUrl?: Map<string, string>;
  } = {},
): NoticeConversation[] {
  return channels.map((channel) =>
    toNoticeConversation(
      channel,
      currentUserId,
      mentorshipByUrl?.get(channel.url),
      avatarByUrl?.get(channel.url),
      enableNeedsResponse,
      {
        now: options.now,
        startedAt: options.startedAtByUrl?.get(channel.url) ?? null,
      },
    ),
  );
}
